import { useTranslation } from 'next-i18next';

import { useCallback } from 'react';

import { GridItem, useBreakpointValue, useToken } from '@chakra-ui/react';

import { IconsNocowanie } from '@nocowanie/icons';

import { DestinationSuggestionItem } from '@app/components/molecules/destination-suggestion-item';
import { SectionCarousel } from '@app/components/organisms/section-carousel';
import { SectionNameEnum } from '@app/enums';
import { PromotedDestinations } from '@app/models';
import { cmsHomepageSlice, commonUiStateSlice, useAppSelector } from '@app/state';

export const DestinationSuggestions = () => {
    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const { t } = useTranslation('home', {
        keyPrefix: 'destinationSuggestions',
    });

    const destinationSuggestionsData: PromotedDestinations = useAppSelector(
        cmsHomepageSlice.selectors.selectSectionData(SectionNameEnum.DestinationSuggestionsSection),
    );

    const gutterWidthDesktop = useToken('spacing', 'gutterWidthDesktop');

    const getSlideWidth = useCallback(
        (x: number) => {
            // we need to calculate the appropriate margin factor value depending on the number of slides
            const marginFactor = Math.round((1 - 1 / x) * 1000) / 1000;

            return isMobileBrowser
                ? `calc(100% / ${x}) !important`
                : `calc(100% / ${x} - ${gutterWidthDesktop} * ${marginFactor}) !important`;
        },
        [isMobileBrowser, gutterWidthDesktop],
    );

    const slideStyles = {
        '& swiper-container': {
            display: 'flex',
            gap: gutterWidthDesktop,
        },
        '& swiper-slide': {
            flexShrink: 0,
            width: {
                base: getSlideWidth(1.1),
                sm: getSlideWidth(1.4),
                md: getSlideWidth(2),
                lg: getSlideWidth(3),
                xl: getSlideWidth(4),
            },
        },
    };

    const slidesCountBreakpoints = {
        base: 1,
        md: 2,
        lg: 3,
        xl: 4,
    };

    const slidesPerView = useBreakpointValue(
        {
            ...slidesCountBreakpoints,
            base: 1.1,
            sm: 1.4,
        },
        {
            fallback: isMobileBrowser ? 'base' : 'xl',
        },
    );
    const slidesPerGroup = useBreakpointValue(slidesCountBreakpoints);

    if (!destinationSuggestionsData) return;

    const slidesElements = destinationSuggestionsData.sections.map(section => ({
        name: section.label,
        slides: section.destinations.map(destination => ({
            id: destination.label,
            body: <DestinationSuggestionItem {...destination} />,
        })),
    }));

    const filters = (destinationSuggestionsData?.sections || []).map(section => {
        const IconComponent = section.icon?.icons ? IconsNocowanie[section.icon.icons] : null;

        return {
            name: section.label,
            icon: (
                <>
                    {IconComponent ? (
                        <IconComponent
                            size="sm"
                            data-testid="destination-suggestions-category-icon"
                        />
                    ) : null}
                </>
            ),
        };
    });

    return (
        <GridItem data-testid={'destination-suggestions-wrapper'} css={slideStyles}>
            <SectionCarousel
                title={destinationSuggestionsData.headline?.headline ?? ''}
                subtitle={destinationSuggestionsData.headline?.subheadline}
                slideHeight={480}
                filters={filters}
                slideElements={slidesElements}
                sliderParams={{
                    spaceBetween: 24,
                    slidesPerView,
                    slidesPerGroup,
                }}
                sliderOverflow={'hidden'}
                isEdgeToEdgeOnMobile={true}
            />
        </GridItem>
    );
};
